import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, Plus, Trash2, Edit, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  DataTable,
  Pagination,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  SearchInput,
  FilterCombobox,
  useAuth,
} from '@aaas/shared';
import type { DataTableColumn, DataTableAction } from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import { usePositionListQuery } from '../../hooks/usePositionQueries';
import positionService from '../../services/position.service';
import { PositionFormDialog } from '../../components/PositionFormDialog';
import { useATSRole } from '../../hooks/useATSRole';
import type { Position } from '../../types';

export function PositionListPage() {
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const { isLeaderOrAbove } = useATSRole();
  const { t } = useTranslation('ats');
  const { page, size, sortBy, order, filters, setPage, setSize, setSort, setFilters } =
    useUrlPagination({ filterKeys: ['search', 'status'] });
  const searchValue = filters.search as string | undefined;
  const statusValue = filters.status as string | undefined;

  const statusOptions = [
    { value: 'active', label: t('positionConfig.statusActive') },
    { value: 'inactive', label: t('positionConfig.statusInactive') },
  ];
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);

  // React Query for Position list
  const {
    data: positionResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = usePositionListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    sortBy,
    order,
    search: searchValue,
    status: statusValue,
    enabled: !!user && !!currentWorkspace,
    refetchInterval: 5000,
  });

  // Extract data from response
  const positions = positionResponse?.data?.items || [];
  const totalItems = positionResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleCreateClick = useCallback(() => {
    setFormMode('create');
    setEditingPosition(null);
    setShowFormDialog(true);
  }, []);

  const handleEditClick = useCallback((record: Position) => {
    setFormMode('edit');
    setEditingPosition(record);
    setShowFormDialog(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setShowFormDialog(false);
    setEditingPosition(null);
  }, []);

  const handleFormSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleViewClick = useCallback(
    (record: Position) => {
      navigate(`/ats/positions/${record.id}`);
    },
    [navigate]
  );

  const handleDeleteClick = useCallback((record: Position) => {
    setSelectedPosition(record);
    setDeleteError(null);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!selectedPosition || !user || !currentWorkspace) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeletingIds((prev) => new Set(prev).add(selectedPosition.id));

    try {
      const token = await user.getIdToken();
      await positionService.deletePosition(selectedPosition.id, currentWorkspace.id, token);
      await refetch();
      setShowDeleteDialog(false);
      setSelectedPosition(null);
    } catch (err: unknown) {
      let errorMessage = t('config.unknownError');
      if (axios.isAxiosError(err)) {
        errorMessage =
          err.response?.data?.message || err.response?.data?.detail || err.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        if (selectedPosition) {
          newSet.delete(selectedPosition.id);
        }
        return newSet;
      });
    }
  };

  const handleDialogClose = () => {
    if (!isDeleting) {
      setShowDeleteDialog(false);
      setSelectedPosition(null);
      setDeleteError(null);
    }
  };

  // Define table columns
  const columns: DataTableColumn<Position>[] = [
    {
      key: 'code',
      title: t('positionConfig.columnCode'),
      width: 120,
      sortable: true,
      render: (value: string) => (
        <span className="font-mono text-sm font-medium text-blue-600">{value}</span>
      ),
    },
    {
      key: 'name',
      title: t('positionConfig.columnName'),
      sortable: true,
      render: (value: string, record: Position) => (
        <button
          onClick={() => handleViewClick(record)}
          className="font-medium text-green-600 hover:text-green-700 hover:underline text-left"
        >
          {value}
        </button>
      ),
    },
    {
      key: 'description',
      title: t('positionConfig.columnDescription'),
      render: (value: string | null) => (
        <span className="text-gray-600 truncate max-w-xs block">{value || '-'}</span>
      ),
    },
    {
      key: 'status',
      title: t('positionConfig.columnStatus'),
      width: 100,
      sortable: true,
      render: (value: string) => (
        <Badge
          className={
            value === 'active'
              ? 'bg-green-100 text-green-800 hover:bg-green-100'
              : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
          }
        >
          {value === 'active'
            ? t('positionConfig.statusActive')
            : t('positionConfig.statusInactive')}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      title: t('config.columnCreated'),
      width: 120,
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  // Define table actions (only for leader+)
  const actions: DataTableAction<Position>[] = isLeaderOrAbove
    ? [
        {
          key: 'edit',
          label: t('config.edit'),
          icon: <Edit className="h-4 w-4" />,
          onClick: handleEditClick,
          inline: true,
        },
        {
          key: 'delete',
          label: t('config.delete'),
          icon: <Trash2 className="h-4 w-4" />,
          loadingIcon: <Loader2 className="h-4 w-4 animate-spin" />,
          onClick: handleDeleteClick,
          loading: (record) => deletingIds.has(record.id),
          destructive: true,
          inline: true,
        },
      ]
    : [];

  if (isLoading && !positionResponse) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Briefcase className="h-8 w-8 text-green-600" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{t('positionConfig.pageTitle')}</h1>
              {isFetching && !isLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-500">{t('positionConfig.pageDescription')}</p>
          </div>
        </div>
        {isLeaderOrAbove && (
          <button
            onClick={handleCreateClick}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('positionConfig.newOne')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <SearchInput
          placeholder={t('positionConfig.searchPlaceholder')}
          onSearch={(value) => setFilters({ ...filters, search: value || undefined })}
          initialValue={searchValue || ''}
        />
        <FilterCombobox
          options={statusOptions}
          value={statusValue}
          onChange={(value) => setFilters({ ...filters, status: value })}
          placeholder={t('positionConfig.columnStatus')}
          searchPlaceholder={t('positionConfig.statusSearchPlaceholder')}
          emptyMessage={t('positionConfig.statusEmpty')}
          className="w-[140px]"
        />
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('positionConfig.loadFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={positions}
          loading={isLoading && !positionResponse}
          actions={actions}
          rowKey={(record) => record.id}
          emptyMessage={t('positionConfig.empty')}
          onSort={setSort}
          sortKey={sortBy}
          sortDirection={order}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={size}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={handleDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('positionConfig.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('positionConfig.deleteBody')}</AlertDialogDescription>
            {deleteError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{deleteError}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleDeleteConfirm();
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.deleting')}
                </>
              ) : (
                t('config.delete')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Position Dialog */}
      <PositionFormDialog
        open={showFormDialog}
        onClose={handleFormClose}
        mode={formMode}
        position={editingPosition}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}
